import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings, Upload } from "lucide-react";
import api, { formatError, fileUrl } from "@/api";
import { toast } from "sonner";

export default function OrgSettings({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    org_name: user.org_name || "",
    org_address: user.org_address || "",
    org_phone: user.org_phone || "",
    working_hours: user.working_hours || "",
    org_image: user.org_image || "",
    city: user.city || "",
    upi_vpa: user.upi_vpa || "",
    platform_commission_per_patient: user.platform_commission_per_patient ?? 0,
    government_schemes: user.government_schemes || [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, org_image: data.path }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const toggleScheme = (scheme) => {
    setForm((s) => ({
      ...s,
      government_schemes: s.government_schemes.includes(scheme)
        ? s.government_schemes.filter((x) => x !== scheme)
        : [...s.government_schemes, scheme],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/org/me", form);
      toast.success("Saved");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" data-testid="org-settings-btn"><Settings className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit {user.org_type === "clinic" ? "Clinic" : "Hospital"} Details</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border-2 border-dashed bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.org_image ? <img src={fileUrl(form.org_image)} alt="" className="w-full h-full object-cover" /> : <Upload className="w-6 h-6 text-slate-400" />}
            </div>
            <label className="text-sm text-sky-600 hover:underline cursor-pointer" data-testid="org-image-upload">
              {uploading ? "Uploading..." : "Upload image"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="org-name-input" />
          </div>
          <div>
            <Label>Address</Label>
            <Input value={form.org_address} onChange={(e) => setForm({ ...form, org_address: e.target.value })} data-testid="org-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Mumbai" data-testid="org-city-input" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.org_phone} onChange={(e) => setForm({ ...form, org_phone: e.target.value })} data-testid="org-phone-input" />
            </div>
          </div>
          <div>
            <Label>UPI VPA (for patient payments)</Label>
            <Input value={form.upi_vpa} onChange={(e) => setForm({ ...form, upi_vpa: e.target.value.trim() })} placeholder="hospital@upi" data-testid="org-upi-input" />
            <p className="text-xs text-slate-500 mt-1">Patients will pay directly to this UPI ID — 0% commission.</p>
          </div>
          <div>
            <Label>Platform Commission Per Patient (₹)</Label>
            <Input type="number" min="0" step="1" value={form.platform_commission_per_patient}
              onChange={(e) => setForm({ ...form, platform_commission_per_patient: e.target.value === "" ? 0 : Number(e.target.value) })}
              data-testid="org-commission-input" placeholder="0" />
            <p className="text-xs text-slate-500 mt-1">Fixed fee deducted per completed patient.</p>
          </div>
          <div>
            <Label>Working Hours</Label>
            <Input value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} placeholder="9 AM - 9 PM" data-testid="org-hours-input" />
          </div>

          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Government Schemes</Label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox 
                  id="rghs" 
                  checked={form.government_schemes.includes("rghs")}
                  onCheckedChange={() => toggleScheme("rghs")}
                />
                <Label htmlFor="rghs" className="font-normal cursor-pointer">RGHS (Rajasthan)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox 
                  id="ayushman" 
                  checked={form.government_schemes.includes("ayushman")}
                  onCheckedChange={() => toggleScheme("ayushman")}
                />
                <Label htmlFor="ayushman" className="font-normal cursor-pointer">Ayushman Bharat</Label>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">Enable schemes to appear in patient searches.</p>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="org-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
